
import { useState, type FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    projectDetails: '',
    budget: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('projectDetails', formData.projectDetails);
      if (formData.budget) {
        formBody.append('budget', formData.budget);
      }

      const response = await fetch('https://readdy.ai/api/form/d4sfu811e6in3mfvkf6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', projectDetails: '', budget: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
            Let's Work Together
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Ready to elevate your YouTube channel with stunning thumbnails? Let's create something amazing together.
          </p>
        </div>

        {/* Contact Content */}
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left Side - Contact Info */}
          <div className="space-y-8">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg shadow-purple-500/50 flex-shrink-0">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Email</h3>
                <p className="text-gray-300">contact.designwithhafeez@gmail.com</p>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl shadow-lg shadow-cyan-500/50 flex-shrink-0">
                <i className="ri-time-line text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Response Time</h3>
                <p className="text-gray-300">Within 24 Hours</p>
              </div>
            </div>

            {/* Availability */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl shadow-lg shadow-pink-500/50 flex-shrink-0">
                <i className="ri-global-line text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Availability</h3>
                <p className="text-gray-300">Worldwide Remote Work</p>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-white">Connect With Me</h3>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/hafeez.dsg/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-purple-600/20 border border-purple-500/30 rounded-full hover:bg-purple-600/40 transition-all duration-300 hover:scale-110 cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a
                  href="https://x.com/AbdulHafee92342"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-purple-600/20 border border-purple-500/30 rounded-full hover:bg-purple-600/40 transition-all duration-300 hover:scale-110 cursor-pointer"
                  aria-label="X (Twitter)"
                >
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="relative p-8 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-3xl border border-purple-500/20">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-5 rounded-3xl blur-xl"></div>
            
            <form onSubmit={handleSubmit} className="relative z-10 space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold mb-2 text-gray-300">
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-purple-900/30 border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors duration-300"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-2 text-gray-300">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-purple-900/30 border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors duration-300"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="projectDetails" className="block text-sm font-semibold mb-2 text-gray-300">
                  Project Details
                </label>
                <textarea
                  id="projectDetails"
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, projectDetails: e.target.value });
                    }
                  }}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-purple-900/30 border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors duration-300 resize-none"
                  placeholder="Tell me about your project..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.projectDetails.length}/500
                </div>
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-semibold mb-2 text-gray-300">
                  Budget (Optional)
                </label>
                <input
                  type="text"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full px-4 py-3 bg-purple-900/30 border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors duration-300"
                  placeholder="Your budget range"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-semibold shadow-lg shadow-purple-500/50 hover:shadow-purple-500/80 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-xl text-green-300 text-center">
                  Message sent successfully! I'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-center">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
